import { useCallback, useEffect, useState } from 'react';
import { Download, Check, Loader2, CloudOff, Trash2, AlertTriangle } from 'lucide-react';
import { checkFits, collectAssets, download, estimateBytes, inspect, remove, type LessonAssets, type OfflineState } from './offlineService';
import { offlineSupported } from './register';
import { useAuthStore } from '../../stores/authStore';

/**
 * Download-for-offline control + status badge for one lesson.
 *
 * The badge is driven by `inspect()` — a real read of Cache Storage — and never
 * by a stored flag, because the browser can evict the audio at any time and a
 * stale "downloaded" badge is worse than none: it is the difference between
 * knowing you have no narration and discovering it after take-off.
 *
 * It also re-inspects when the tab regains focus, which is when an eviction
 * that happened in the background becomes visible.
 */
const fmtMB = (b: number) => (b >= 1024 * 1024 * 1024
  ? `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`
  : `${Math.max(1, Math.round(b / 1024 / 1024))} MB`);

export function OfflineLessonButton({ lessonId, compact = false }: { lessonId: string; compact?: boolean }) {
  // Platform admins only, by request.
  //
  // This is a PRESENTATION gate and is the right boundary for this particular
  // feature: downloading caches content the signed-in user is already entitled
  // to fetch and play, so hiding the control exposes nothing new and removing
  // it grants nothing new. If offline access ever needs to be a licensing or
  // entitlement boundary rather than a rollout control, the gate has to move
  // server-side — the client cannot be the authority on what a user may keep.
  const isAdmin = useAuthStore((s) => s.user?.is_admin) ?? false;
  const [assets, setAssets] = useState<LessonAssets | null>(null);
  const [state, setState] = useState<OfflineState | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sizeBytes, setSizeBytes] = useState<number | null>(null);

  const refresh = useCallback(async (a?: LessonAssets) => {
    const target = a ?? assets;
    if (!target) return;
    try { setState(await inspect(target)); } catch { /* leave the last known state */ }
  }, [assets]);

  useEffect(() => {
    if (!isAdmin || !offlineSupported()) return;
    let alive = true;
    (async () => {
      try {
        const a = await collectAssets(lessonId);
        if (!alive) return;
        setAssets(a);
        setState(await inspect(a));
        // Show the cost BEFORE the user commits — narration is the heavy part
        // and "Download" with no number attached is how storage fills up by
        // surprise.
        estimateBytes(a).then((b) => { if (alive) setSizeBytes(b); }).catch(() => {});
      } catch {
        // Offline right now and the manifest is not cached — we simply cannot
        // know. Show nothing rather than guess.
      }
    })();
    return () => { alive = false; };
  }, [lessonId]);

  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const start = async () => {
    if (!assets) return;
    setBusy(true); setError(null); setProgress({ done: 0, total: 0 });
    try {
      // A quota failure mid-download leaves a lesson that looks downloaded and
      // goes silent partway through. Refuse up front instead.
      const fit = await checkFits(sizeBytes);
      if (!fit.fits) {
        setError('space');
        setBusy(false); setProgress(null);
        return;
      }
      const next = await download(assets, (done, total) => setProgress({ done, total }));
      setState(next);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // eslint-disable-next-line no-console
      console.error('[offline] download failed:', e);
      setError(
        msg.startsWith('download_failed:401') || msg.startsWith('download_failed:403') ? 'auth'
        : msg.startsWith('download_failed') ? 'network'
        : /quota/i.test(msg) ? 'space'
        : msg.slice(0, 60),
      );
      await refresh();
    } finally {
      setBusy(false); setProgress(null);
    }
  };

  const clear = async () => {
    if (!assets) return;
    setBusy(true);
    try { await remove(assets); } finally { setBusy(false); await refresh(); }
  };

  if (!isAdmin) return null;
  if (!offlineSupported()) return null;
  if (!assets || !state) return null;

  // A lesson whose audio manifest is empty would otherwise satisfy "every
  // expected asset is cached" with ZERO audio files and show the offline badge
  // — true by the letter, and a lie in the way that matters, because the badge
  // is read as "narration will play". Say what is actually there instead.
  const hasNarration = assets.audioUrls.length > 0;
  if (!hasNarration) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ws-ink-3)]" title="This lesson has no synthesized narration yet, so there is nothing to take offline.">
        <CloudOff className="w-3.5 h-3.5" aria-hidden="true" />
        No narration yet
      </span>
    );
  }

  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  // Fully downloaded — the badge the request was actually about.
  if (state.status === 'ready' && !busy) {
    return (
      <span className="inline-flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 rounded-md bg-[var(--ws-accent-quiet)] px-1.5 py-0.5 text-[11px] font-medium leading-tight text-[var(--ws-accent-ink)]"
          title={`Available offline — ${state.total} files, narration included`}
        >
          <Check className="w-3 h-3" aria-hidden="true" />
          Offline
        </span>
        {!compact && (
          <button
            type="button" onClick={clear} disabled={busy}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-[var(--ws-ink-3)] hover:text-[var(--ws-ink)] disabled:opacity-50"
            title="Remove the downloaded copy"
          >
            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
            Remove
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button" onClick={start} disabled={busy}
        className="inline-flex items-center gap-1 rounded-md border border-[var(--ws-border)] px-1.5 py-0.5 text-[11px] font-medium leading-tight text-[var(--ws-ink-2)] hover:bg-[var(--ws-accent-quiet)] disabled:opacity-60"
        title={`Download this lesson — slides and narration — so it plays with no internet${sizeBytes != null ? ` (${fmtMB(sizeBytes)})` : ''}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              : state.status === 'partial' ? <CloudOff className="w-3.5 h-3.5" aria-hidden="true" />
              : <Download className="w-3.5 h-3.5" aria-hidden="true" />}
        {busy ? `${pct}%` : state.status === 'partial' ? 'Finish' : 'Offline'}
      </button>
      {error && (
        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--ws-danger,#B3261E)]" role="status">
          <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
          {error === 'network' ? "Couldn't download — check your connection"
            : error === 'space' ? 'Not enough space on this device'
            : error === 'auth' ? 'Session expired — sign in again'
            : error}
        </span>
      )}
    </span>
  );
}

export default OfflineLessonButton;
